import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiFileText, FiLink, FiPlus, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { ticketsClient, type Ticket, type TicketCategory } from "../../Pages/Tickets/ticketsClient";
import { getUserDirectory, type DirectoryEntry } from "../Account/accountClient";
import { AssigneePicker } from "./AssigneePicker";

/**
 * Create-ticket modal, shared by the Tickets page and the content/page editors. When opened from an
 * editor, `linkedContentId` pre-links the document and `defaultCategory` ("contenu") is pre-selected.
 * `initialTitle`/`initialDescription` prefill the form (deep-link ?nouveau=1 from the public preview
 * widget) : the operator can still edit both before submitting. Assignees are optional : leaving
 * them empty routes the ticket to the category's default assignee(s).
 */
export const TicketCreateModal: React.FC<{
	open: boolean;
	onClose: () => void;
	onCreated?: (ticket: Ticket) => void;
	linkedContentId?: string | null;
	linkedContentType?: string | null;
	linkedContentTitle?: string | null;
	defaultCategory?: string;
	initialTitle?: string;
	initialDescription?: string;
}> = ({ open, onClose, onCreated, linkedContentId, linkedContentType, linkedContentTitle, defaultCategory, initialTitle, initialDescription }) => {
	const [categories, setCategories] = useState<TicketCategory[]>([]);
	const [directory, setDirectory] = useState<DirectoryEntry[]>([]);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [category, setCategory] = useState(defaultCategory ?? "autre");
	const [assignees, setAssignees] = useState<string[]>([]);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!open) return;
		setTitle(initialTitle ?? "");
		setDescription(initialDescription ?? "");
		setCategory(defaultCategory ?? (linkedContentId ? "contenu" : "autre"));
		setAssignees([]);
		ticketsClient.categories().then(setCategories).catch(() => setCategories([]));
		getUserDirectory().then(setDirectory).catch(() => setDirectory([]));
	}, [open, defaultCategory, linkedContentId, initialTitle, initialDescription]);

	const emails = useMemo(
		() => directory.filter((d) => !!d.email).map((d) => ({ email: d.email as string, name: d.name || d.email })),
		[directory]
	);

	if (!open) return null;

	const submit = async () => {
		if (!title.trim()) {
			toast.error("Le titre est requis.");
			return;
		}
		setBusy(true);
		try {
			const ticket = await ticketsClient.create({
				title: title.trim(),
				description: description.trim(),
				category,
				linkedContents: linkedContentId
					? [{ contentId: linkedContentId, contentType: linkedContentType ?? "", title: linkedContentTitle ?? null }]
					: undefined,
				assignees
			});
			toast.success("Ticket créé.");
			onCreated?.(ticket);
			onClose();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "La création du ticket a échoué.");
		} finally {
			setBusy(false);
		}
	};

	// Portal to <body> + z above the sidebar (relative z-[1000]) so the backdrop covers the whole screen.
	return createPortal(
		<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
			<div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
				<div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-5 py-4">
					<div className="flex items-center gap-3">
						<span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#005CA9]/10 text-[#005CA9]">
							<FiFileText className="h-4 w-4" />
						</span>
						<div>
							<h2 className="text-base font-bold text-slate-800">Nouveau ticket</h2>
							<p className="text-xs text-slate-400">Décrivez la demande : l'équipe est notifiée en temps réel.</p>
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
						title="Fermer"
					>
						<FiX />
					</button>
				</div>
				<div className="space-y-4 px-5 py-5">
					{linkedContentId && (
						<div className="flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
							<FiLink className="h-3.5 w-3.5 shrink-0" />
							<span className="min-w-0 truncate">
								Lié au contenu{" "}
								<span className="font-semibold">{linkedContentTitle || linkedContentId}</span>
							</span>
						</div>
					)}
					<label className="block">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Titre</span>
						<input
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder="Résumé du problème"
							autoFocus
							className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:bg-white focus:outline-none"
						/>
					</label>
					<label className="block">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Catégorie</span>
						<select
							value={category}
							onChange={(e) => setCategory(e.target.value)}
							className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 transition focus:border-slate-400 focus:bg-white focus:outline-none"
						>
							{categories.map((c) => (
								<option key={c.key} value={c.key}>
									{c.label}
								</option>
							))}
						</select>
					</label>
					<label className="block">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</span>
						<textarea
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={4}
							placeholder="Détails, étapes pour reproduire, contexte…"
							className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:bg-white focus:outline-none"
						/>
					</label>
					<div>
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
							Assigner à <span className="font-normal normal-case text-slate-400">(facultatif : sinon assigné par défaut de la catégorie)</span>
						</span>
						<div className="mt-1">
							<AssigneePicker value={assignees} onChange={setAssignees} options={emails} />
						</div>
					</div>
				</div>
				<div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3.5">
					<button
						type="button"
						onClick={onClose}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={submit}
						disabled={busy || !title.trim()}
						className="inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87] disabled:cursor-not-allowed disabled:opacity-50"
					>
						<FiPlus className="h-4 w-4" /> {busy ? "Création…" : "Créer le ticket"}
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default TicketCreateModal;
